import { useState } from "react";
import * as XLSX from "xlsx";
import { uploadExcelFile } from "../../../services/AdminService";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

const UploadExcel = () => {
  const [data, setData] = useState([]);
  const [fileName, setFileName] = useState("");
  const [selectedFile, setSelectedFile] = useState(null);
  const [uploadResponse, setUploadResponse] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Get both clientId and userId from session storage
  const clientId = sessionStorage.getItem("clientId");
  const userId = sessionStorage.getItem("userId");

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate file type
    const validExtensions = ['.xlsx', '.xls', '.csv'];
    const fileExtension = file.name.toLowerCase().substring(file.name.lastIndexOf('.'));

    if (!validExtensions.includes(fileExtension)) {
      toast.error("❌ Invalid file format! Please select an Excel file (.xlsx, .xls, or .csv)");
      e.target.value = ''; // Clear the input
      return;
    }

    // Validate file size (10MB limit)
    const maxSize = 10 * 1024 * 1024; // 10MB in bytes
    if (file.size > maxSize) {
      toast.error("❌ File too large! Please select a file smaller than 10MB");
      e.target.value = ''; // Clear the input
      return;
    }

    setFileName(file.name);
    setSelectedFile(file);
    setUploadResponse(null); // Clear previous response

    const reader = new FileReader();
    reader.onload = (evt) => {
      try {
        const binaryStr = evt.target.result;
        const workbook = XLSX.read(binaryStr, { type: "binary" });
        const sheetName = workbook.SheetNames[0];
        const sheet = workbook.Sheets[sheetName];
        const parsedData = XLSX.utils.sheet_to_json(sheet, {
          header: 1,
          raw: false,
          defval: "",
        });

        if (parsedData.length === 0) {
          toast.error("❌ Excel file is empty! Please check your file and try again.");
          return;
        }

        if (parsedData.length < 2) {
          toast.error("❌ Excel file must contain a header row and at least one data row.");
          return;
        }

        setData(parsedData);
        toast.success(`✅ Excel file loaded successfully! Found ${parsedData.length - 1} data rows.`);
      } catch (error) {
        toast.error("❌ Failed to read Excel file! Please ensure the file is not corrupted.");
        console.error("File reading error:", error);
      }
    };

    reader.onerror = () => {
      toast.error("❌ Error reading file! Please try again with a different file.");
    };

    reader.readAsBinaryString(file);
  };

  const handleUseSample = () => {
    const sampleData = [
      [
        "User Name",
        "Email Id",
        "Contact Number",
        "Contract Type",
        "Device ID",
        "Area Code",
        "Product Name",
        "Plan Start",
        "Plan End",
        "RMS Balance",
        "Issues",
      ],
      [
        "John Doe",
        "john.doe@example.com",
        "9876543210",
        "AMC",
        "DEV001",
        "AREA01",
        "Air Conditioner",
        "2025-01-01",
        "2025-12-31",
        "4",
        "Cooling performance, Refrigerant-related",
      ],
      [
        "Jane Smith",
        "jane.smith@example.com",
        "9876543211",
        "AMC",
        "DEV002",
        "AREA02",
        "Refrigerator",
        "2025-01-01",
        "2025-12-31",
        "4",
        "Temperature control, Noise issues",
      ],
    ];

    setFileName("SampleData.xlsx");
    setData(sampleData);
    setSelectedFile(null);
    setUploadResponse(null);
  };

  const handleReset = () => {
    setFileName("");
    setData([]);
    setSelectedFile(null);
    setUploadResponse(null);
    setIsUploading(false);

    // Clear file input
    const fileInput = document.querySelector('input[type="file"]');
    if (fileInput) fileInput.value = '';
  };

  const handleUploadToServer = async () => {
    // Validation checks
    if (!selectedFile) {
      toast.error("❌ Please select an Excel file to upload first!");
      return;
    }

    if (!clientId || !userId) {
      toast.error("❌ Session expired! Please login again to continue.");
      return;
    }

    if (data.length < 2) {
      toast.error("❌ No data found in Excel file! Please check your file format.");
      return;
    }

    setIsUploading(true);
    setUploadResponse(null);

    try {
      const result = await uploadExcelFile(selectedFile, clientId, userId);
      setUploadResponse(result);

      if (result.status) {
        // Success case
        const createdCount = result.data?.created_rows || 0;
        const processedCount = result.data?.processed_rows || 0;
        const errorCount = result.data?.error_rows || 0;
        if (errorCount > 0) {
          toast.warning(
            `Upload completed with issues. ${createdCount} records created, ${errorCount} rows failed.`
          );
        } else {
          toast.success(
            `Upload successful! ${createdCount} records created.`
          );
        }
      } else {
        // Handle different types of failures
        if (result.error_type === 'duplicate_entries_found') {
          // Special handling for duplicate entries
          const duplicateCount = result.duplicate_details?.total_duplicates || 0;
          toast.warning(
            `⚠️ Upload stopped! Found ${duplicateCount} existing records. Please use 'Update Records' menu for existing data or remove duplicates from your Excel file.`
          );
        } else {
          // General failure
          toast.error(`❌ Upload failed: ${result.message}`);
        }
      }
    } catch (error) {
      console.error("Upload Error:", error);

      // Handle different types of errors
      if (error.response) {
        // Server responded with error status
        const status = error.response.status;
        const serverMessage = error.response.data?.message;

        if (status === 400) {
          toast.error(`Invalid request: ${serverMessage || 'Please check your file format and try again.'}`);
        } else if (status === 403) {
          toast.error("Access denied! You don't have permission to upload files for this client.");
        } else if (status === 409) {
          // Conflict - duplicate entries
          toast.warning("Upload stopped due to existing records. Please use 'Update Records' menu for modifications.");
        } else if (status === 413) {
          toast.error("File too large! Please reduce file size and try again.");
        } else if (status >= 500) {
          toast.error("Server error occurred! Please try again later or contact support.");
        } else {
          toast.error(`Upload failed: ${serverMessage || 'Unknown server error occurred.'}`);
        }
      } else if (error.request) {
        // Network error
        toast.error("Network error! Please check your internet connection and try again.");
      } else {
        // Other errors
        toast.error("Unexpected error occurred! Please try again or contact support.");
      }

      setUploadResponse({
        status: false,
        message: error.response?.data?.message || error.message || "Upload failed"
      });
    } finally {
      setIsUploading(false);
    }
  };

  const renderUploadResponse = () => {
  if (!uploadResponse) return null;

  const responseData = uploadResponse.data || {};
  const hasErrors = (responseData.error_rows || 0) > 0;

  if (uploadResponse.status) {
    // if (!uploadResponse) return null;
    // if (uploadResponse.status) {
    //   const responseData = uploadResponse.data || {};
    //   const hasErrors = (responseData.error_rows || 0) > 0;
      return (
        <div className={`alert mt-4 ${hasErrors ? "alert-warning" : "alert-success"}`}>
          <h5 className="alert-heading">
            {hasErrors ? "Upload Completed with Issues" : "Upload Successful"}
          </h5>
          <div className="row mt-3">
            <div className="col-md-3">
              <strong>New Records:</strong> {responseData.created_rows || 0}
            </div>

            <div className="col-md-3">
              <strong>Total Rows:</strong> {responseData.length > 0 ? data.length - 1 : 0}
            </div>

            <div className="col-md-3">
              <strong>Skipped:</strong> {responseData.skipped_rows || 0}
            </div>

            <div className="col-md-3">
              <strong>Failed:</strong> {responseData.error_rows || 0}
            </div>
          </div>

          {/* Show created items */}
          {responseData.created_users?.length > 0 && (
            <div className="mt-3">
              <strong>New Users Created:</strong>
              <ul className="mb-0 mt-1">
                {responseData.created_users.slice(0, 5).map((user, idx) => (
                  <li key={idx}>{user}</li>
                ))}
                {responseData.created_users.length > 5 && (
                  <li className="text-muted">... and {responseData.created_users.length - 5} more</li>
                )}
              </ul>
            </div>
          )}

          {responseData.errors?.length > 0 && (
            <div className="mt-4">
              <div className="alert alert-warning">
                <strong>⚠ Records not added:</strong>

                <ul className="mt-2 mb-0">
                  {responseData.errors.map((err, idx) => {
                    // extract name + reason from error string
                    const parts = err.split(" - ");
                    return (
                      <li key={idx}>
                        <strong>{parts[0]}</strong>
                        <br />
                        <small className="text-muted">
                          Reason: {parts[1] || err}
                        </small>
                      </li>
                    );
                  })}
                </ul>
              </div>
            </div>
          )}
        </div>
      );
    } else {
      // Error response
      if (uploadResponse.error_type === 'duplicate_entries_found') {
        const duplicateDetails = uploadResponse.duplicate_details || {};
        return (
          <div className="alert alert-warning mt-4">
            <h5 className="alert-heading">⚠️ Existing Entry Found</h5>
            <div className="mb-3">
              <strong>Total Duplicates:</strong> {duplicateDetails.total_duplicates || 0}
              {duplicateDetails.duplicate_users > 0 && (
                <span className="ms-3"><strong>Users:</strong> {duplicateDetails.duplicate_users}</span>
              )}
              {duplicateDetails.duplicate_subscriptions > 0 && (
                <span className="ms-3"><strong>Subscriptions:</strong> {duplicateDetails.duplicate_subscriptions}</span>
              )}
            </div>

            <div className="bg-light p-3 rounded">
              <h6>📋 Recommended Actions:</h6>
              <ul className="mb-0">
                <li>Use the <strong>'Update Records'</strong> menu to modify existing data</li>
                <li>Remove existing entries from your Excel file and re-upload</li>
                <li>Contact your system administrator for bulk update assistance</li>
              </ul>
            </div>

            {duplicateDetails.entries?.length > 0 && (
              <details className="mt-3">
                <summary className="btn btn-outline-secondary btn-sm">View Duplicate Details</summary>
                {responseData.errors?.length > 0 && (
                  <div className="mt-3">
                    <strong>⚠️ Reason:</strong>
                    <ul className="mb-0 mt-1">
                      {responseData.errors.slice(0, 5).map((err, idx) => (
                        <li key={idx}>{err}</li>
                      ))}
                      {responseData.errors.length > 5 && (
                        <li className="text-muted">... and {responseData.errors.length - 5} more</li>
                      )}
                    </ul>
                  </div>
                )}
                <div className="mt-2">
                  {duplicateDetails.entries.slice(0, 10).map((entry, idx) => (
                    <div key={idx} className="small text-muted">
                      Row {entry.row}: {entry.name} ({entry.identifier}) - {entry.message}
                    </div>
                  ))}
                  {duplicateDetails.entries.length > 10 && (
                    <div className="small text-muted">... and {duplicateDetails.entries.length - 10} more duplicates</div>
                  )}
                </div>
              </details>
            )}
          </div>
        );
      } else {
        // General error
        return (
          <div className="alert alert-danger mt-4">
            <h5 className="alert-heading">Upload Failed</h5>
            <p className="mb-0">{uploadResponse.message}</p>
            {uploadResponse.error && (
              <small className="text-muted d-block mt-2">
                Technical Details: {uploadResponse.error}
              </small>
            )}
          </div>
        );
      }
    }
  };

  return (
    <div className="container mt-5">
      <ToastContainer
        position="top-right"
        autoClose={6000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />

      <h2 className="mb-4 text-center">Upload Excel To Setup Data</h2>

      {/* Important Notice */}
      <div className="alert alert-info mb-4">
        <p className="mb-0">
          This upload feature is designed <strong>only for adding new records</strong>. </p>
        <p className="mb-0"> If you need to update existing data, please use the <strong>'Update Records'</strong> menu from the main dashboard.
        </p>
      </div>

      <div className="mb-4 text-center">
        <input
          type="file"
          accept=".xlsx,.xls,.csv"
          className="form-control mx-auto w-50"
          onChange={handleFileUpload}
          disabled={isUploading}
        />

        <div className="d-flex justify-content-center gap-3 flex-wrap mt-3">
          <button
            className="btn btn-outline-primary"
            onClick={handleUseSample}
            disabled={isUploading}
          >
            See Sample Data
          </button>
          <button
            className="btn btn-outline-secondary"
            onClick={handleReset}
            disabled={isUploading}
          >
            Reset
          </button>
          <button
            className="btn btn-success"
            onClick={handleUploadToServer}
            disabled={!clientId || !userId || !selectedFile || isUploading}
          >
            {isUploading ? (
              <>
                <span className="spinner-border spinner-border-sm me-2" role="status" aria-hidden="true"></span>
                Uploading...
              </>
            ) : (
              <>Upload to Server</>
            )}
          </button>
        </div>
      </div>

      {fileName && (
        <p className="text-muted text-center">
          📁 Selected File: <strong>{fileName}</strong>
        </p>
      )}

      {/* Upload Response Display */}
      {renderUploadResponse()}

      {/* Data Preview Table */}
      {data.length > 0 && (
        <div className="table-responsive mt-4">
          <h5>Data Preview ({data.length - 1} rows):</h5>
          <table className="table table-bordered table-striped">
            <thead className="table-dark">
              <tr>
                {data[0].map((heading, i) => (
                  <th key={i}>{heading}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.slice(1, 11).map((row, ri) => (
                <tr key={ri}>
                  {row.map((cell, ci) => {
                    const header = data[0][ci];
                    const isDateColumn =
                      header === "Plan Start" ||
                      header === "Plan End" ||
                      header === "AMC Starting Date" ||
                      header === "AMC Ending Date";

                    let formattedCell = cell;

                    if (isDateColumn && cell) {
                      if (!isNaN(cell)) {
                        // Excel serial date → JS date
                        const date = new Date((cell - 25569) * 86400 * 1000);
                        formattedCell = date.toISOString().split("T")[0];
                      } else if (typeof cell === "string") {
                        const parsedDate = new Date(cell);
                        if (!isNaN(parsedDate)) {
                          const yyyy = parsedDate.getFullYear();
                          const mm = String(parsedDate.getMonth() + 1).padStart(2, "0");
                          const dd = String(parsedDate.getDate()).padStart(2, "0");
                          formattedCell = `${yyyy}-${mm}-${dd}`;
                        }
                      }
                    }

                    return (
                      <td key={ci} className={cell === "" ? "text-muted" : ""}>
                        {formattedCell || <em>empty</em>}
                      </td>
                    );
                  })}
                </tr>
              ))}
              {data.length > 11 && (
                <tr>
                  <td colSpan={data[0].length} className="text-center text-muted">
                    ... and {data.length - 11} more rows (showing first 10 for preview)
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default UploadExcel;